import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Bell, CheckCircle, Clock, UserPlus, Award, CreditCard, Check, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Notifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchNotifications();
    }, []);

    const fetchNotifications = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/notifications`, { withCredentials: true });
            setNotifications(response.data);
        } catch (error) {
            toast.error('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (id) => {
        try {
            await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, { withCredentials: true });
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error('Failed to mark as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await axios.put(`${API_URL}/api/notifications/mark-all-read`, {}, { withCredentials: true });
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            toast.success('All notifications marked as read');
        } catch (error) {
            toast.error('Failed to mark all as read');
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'waiting_list':
            case 'student_approved':
                return <UserPlus className="h-5 w-5 text-[#D97706]" />;
            case 'leave_request':
            case 'leave_approved':
            case 'leave_rejected':
                return <Clock className="h-5 w-5 text-[#F59E0B]" />;
            case 'payment':
                return <CreditCard className="h-5 w-5 text-[#10B981]" />;
            default:
                return <Bell className="h-5 w-5 text-[#064E3B]" />;
        }
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="notifications-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Notifications</h1>
                        <p className="text-[#4B5563]">{unreadCount} unread notifications</p>
                    </div>
                    {unreadCount > 0 && (
                        <Button variant="outline" onClick={markAllAsRead} data-testid="mark-all-read">
                            <Check className="h-4 w-4 mr-2" /> Mark All as Read
                        </Button>
                    )}
                </div>

                <div className="space-y-3">
                    {notifications.length > 0 ? (
                        notifications.map((notification) => (
                            <Card 
                                key={notification.id} 
                                className={`card-surface transition-all cursor-pointer ${!notification.read ? 'border-l-4 border-l-[#064E3B] bg-[#064E3B]/5' : ''}`}
                                onClick={() => !notification.read && markAsRead(notification.id)}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-start space-x-4">
                                        <div className="h-10 w-10 rounded-full bg-[#FAF9F6] flex items-center justify-center flex-shrink-0">
                                            {getIcon(notification.type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between">
                                                <h3 className={`font-medium ${!notification.read ? 'text-[#1F2937]' : 'text-[#4B5563]'}`}>
                                                    {notification.title}
                                                </h3>
                                                <span className="text-xs text-[#4B5563] tabular-nums flex-shrink-0 ml-2">
                                                    {new Date(notification.created_at).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <p className="text-sm text-[#4B5563] mt-1">{notification.message}</p>
                                            {notification.link && <Link to={notification.link} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-[#B45309] hover:underline" data-testid={`notification-link-${notification.id}`}>Open Books tab <ArrowRight className="h-3.5 w-3.5" /></Link>}
                                            {!notification.read && (
                                                <Badge className="mt-2 bg-[#064E3B]">New</Badge>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    ) : (
                        <Card className="card-surface">
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <Bell className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                <p className="text-[#4B5563]">No notifications yet</p>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default Notifications;
